<template>  
  <CCol cols="6" xl="6" lg="8" md="12" sm="12" class="pb-0 pr-0 pl-0">
    <div class="d-flex">
      <div class="flex-grow-1">
        <CRow class="m-0">
          <CCol cols="12" xl="12" lg="12" md="12" sm="12" class="pt-0 pb-0">
            <div class="d-flex align-items-center">
              <b-datepicker v-model="dateFrom"
                            icon="calendar-day"
                            icon-pack="fas"
                            :first-day-of-week="1"
                            :show-week-number="true"
                            :min-date="minDateFrom"
                            :max-date="maxDateFrom"
                            :years-range="[-3, 10]"
                            :placeholder="$t('common.Date_from')"
                            @input="setMinDateBoundary(); emitDateFiltersSet();"
                            class="d-inline-block align-middle mr-2"
                            :disabled="dateFiltersDisabled">
                <CButton color="primary" @click="setDateFromValue(true); emitDateFiltersSet();" :disabled="dateFiltersDisabled">
                  <i class="fas fa-calendar-day mr-1"/>{{$t('Today')}}
                </CButton>           
              </b-datepicker>
              <CButton color="secondary" @click="setDateFromValue(false); emitDateFiltersSet();" :disabled="dateFiltersDisabled" class="reset m-0">
                <i class="fas fa-undo"/>
              </CButton>             
            </div>
          </CCol>
        </CRow>
      </div>
      <div class="flex-grow-1">
        <CRow class="m-0">
          <CCol cols="12" xl="12" lg="12" md="12" sm="12" class="pt-0 pb-0">
            <div class="d-flex align-items-center">
              <b-datepicker v-model="dateTo"
                            icon="calendar-day"
                            icon-pack="fas"
                            :first-day-of-week="1"
                            :show-week-number="true"
                            :min-date="minDateTo"
                            :max-date="maxDateTo"
                            :years-range="[-3, 10]"
                            :placeholder="$t('common.Date_to')"
                            @input="setMaxDateBoundary(); emitDateFiltersSet();"
                            class="d-inline-block align-middle mr-2"
                            :disabled="dateFiltersDisabled">
                <CButton color="primary" @click="setDateToValue(true); emitDateFiltersSet();" :disabled="dateFiltersDisabled">
                  <i class="fas fa-calendar-day mr-1"/>{{$t('Today')}}
                </CButton>          
              </b-datepicker>
              <CButton color="secondary" @click="setDateToValue(true); emitDateFiltersSet();" :disabled="dateFiltersDisabled" class="reset m-0">
                <i class="fas fa-undo"/>
              </CButton>             
            </div>
          </CCol>          
        </CRow>
      </div>
    </div>      
  </CCol>
</template>

<script>
import Multiselect from 'vue-multiselect';
import { DateTime } from "luxon";

export default {
  name: 'DateFilters',
  props: ['parent'],
  components: {
    Multiselect
  },  
  data() {
    return {
      dateFrom: null,
      dateTo: null,
      minDateTo: null,
      maxDateTo: null,
      minDateFrom: null,
      maxDateFrom: null,
      dateFiltersDisabled: false,
      dateFiltersDisabledInternal: 1000,
      dateFromYearAgoParents: [
        'ct_topics_insights',
        'ct_subtopics_insights'
      ],
      dateFromThreeMonthsAgoParents: [
        'bb_insights',
        'bb_item_insights',
        'gs_insights',
        'sm_employees_insights',
        'sm_shop_insights',
        'sq_categories_insights',
        'sq_category_insights',
        'sq_subcategory_insights'
      ]
    }
  },
  methods: {    
    emitDateFiltersSet() {
      // Disable the date filter
      this.dateFiltersDisabled = true;

      // Save date filters to localStorage as formatted strings
      localStorage.setItem('dateFilters', JSON.stringify({
        date_from: this.$luxon(this.dateFrom.toISOString(), "yyyy-MM-dd"),
        date_to: this.$luxon(this.dateTo.toISOString(), "yyyy-MM-dd")        
      }));

      // Emit an event with the current date filters (as Date objects)
      this.$bus.$emit(this.parent + '_date_filters_set', {
        date_from: this.dateFrom,
        date_to: this.dateTo
      });

      // Wait 1000ms and enable the date filters
      setTimeout(function() {
        this.dateFiltersDisabled = false;
      }.bind(this), this.dateFiltersDisabledInternal)      
    },
    setMinDateBoundary() {
      // Set the minimum selectable date for the 'dateTo' picker
      this.minDateTo = this.dateFrom;      
    },
    setMaxDateBoundary() {
      // Set the maximum selectable date for the 'dateFrom' picker
      this.maxDateFrom = this.dateTo;
    },
    setDateToValue(isToday) {
      // If isToday is true, set dateTo to today, otherwise clear the value
      this.dateTo = (isToday) ? new Date() : null;      
      // Always update the maximum allowed dateFrom value to today
      this.maxDateFrom = new Date();

    },
    setDateFromValue(isToday) {
      if(isToday) {
        // If isToday is true, set dateFrom to today
        this.dateFrom = new Date();
        // If dateTo is set, also update it to today
        if(this.dateTo) this.dateTo = new Date();
        // Update boundaries to today
        this.minDateTo = new Date();
        this.maxDateFrom = new Date();
      } else {
        // Set dateFrom to today
        this.dateFrom = new Date();
        
        // If parent is in the 1-year group, subtract 1 year
        if(this.dateFromYearAgoParents.includes(this.parent)) {
          this.dateFrom.setFullYear(this.dateFrom.getFullYear() - 1);
        // If parent is in the 3-month group, subtract 3 months
        } else if(this.dateFromThreeMonthsAgoParents.includes(this.parent)) {
          this.dateFrom.setMonth(this.dateFrom.getMonth() - 3);
        }
        
        // If dateTo is set, update maxDateFrom to dateTo, otherwise to today
        (this.dateTo) ? this.maxDateFrom = this.dateTo : new Date();
        // Set the minimum selectable date for dateTo
        this.minDateTo = this.dateFrom;   
      }      
    }     
  },
  mounted: function() {
    // Set maxDateTo to today
    this.maxDateTo = new Date();

    if(localStorage.getItem('dateFilters') !== null) {
      // Restore date filters from localStorage
      let dateFilters = JSON.parse(localStorage.getItem('dateFilters'));

      // Convert stored date strings back to JS Date objects
      this.dateFrom = DateTime.fromFormat(dateFilters.date_from, "yyyy-MM-dd").toJSDate();
      this.dateTo = DateTime.fromFormat(dateFilters.date_to, "yyyy-MM-dd").toJSDate();      
      // Update min/max boundaries based on restored values
      this.setMinDateBoundary();
      this.setMaxDateBoundary();
      
    } else {
      // Initialize dateFrom and dateTo to today
      this.dateFrom = new Date();
      this.dateTo = new Date();
      this.maxDateFrom = new Date();

      // Adjust dateFrom based on parent type (1 year or 3 months ago)
      if(this.dateFromYearAgoParents.includes(this.parent)) {
        // Subtract 1 year for specific parent types
        this.dateFrom.setFullYear(this.dateFrom.getFullYear() - 1);
      } else if(this.dateFromThreeMonthsAgoParents.includes(this.parent)) {
        // Subtract 3 months for other parent types
        this.dateFrom.setMonth(this.dateFrom.getMonth() - 3);
      }
    }

    // Emit initial date filter values
    this.emitDateFiltersSet();
  }
}
</script>